/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AuthUser } from "../types";
import {
  api,
  ApiError,
  type RegistrationInput,
  setCsrfToken,
} from "../lib/api";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  expiresAt: string | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (input: RegistrationInput) => Promise<string>;
  logout: () => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<string>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setUser(null);
    setExpiresAt(null);
    setCsrfToken(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const session = await api.session();
      if (!session.authenticated) {
        clearSession();
        return;
      }
      setUser(session.user);
      setExpiresAt(session.expiresAt);
      setCsrfToken(session.csrfToken);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        clearSession();
        return;
      }
      clearSession();
    }
  }, [clearSession]);

  useEffect(() => {
    let active = true;
    api.session()
      .then((session) => {
        if (!active) return;
        if (!session.authenticated) {
          setUser(null);
          setExpiresAt(null);
          setCsrfToken(null);
          return;
        }
        setUser(session.user);
        setExpiresAt(session.expiresAt);
        setCsrfToken(session.csrfToken);
      })
      .catch(() => {
        if (!active) return;
        setUser(null);
        setExpiresAt(null);
        setCsrfToken(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => clearSession();
    window.addEventListener("yieldboost:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("yieldboost:unauthorized", handleUnauthorized);
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      expiresAt,
      login: async (email, password) => {
        const session = await api.login(email, password);
        setUser(session.user);
        setExpiresAt(session.expiresAt);
        setCsrfToken(session.csrfToken);
        return session.user;
      },
      register: async (input) => (await api.register(input)).message,
      logout: async () => {
        try {
          await api.logout();
        } finally {
          clearSession();
        }
      },
      changePassword: async (currentPassword, newPassword) =>
        (await api.changePassword(currentPassword, newPassword)).message,
      refresh,
    }),
    [clearSession, expiresAt, loading, refresh, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
